export function svgToDataUri(svg: string): string {
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

// Pattern SVGs encode fills two different ways in the wild (confirmed by fetching real files,
// not assumed): a plain `fill="#rrggbb"` attribute (e.g. patterns/dots/dots_2.svg), or a
// `<style>.st0{fill:#rgb;}</style>` CSS class referenced elsewhere in the doc (e.g.
// patterns/dots/circles_1.svg) - and either form may use 3-digit shorthand hex. Comparing colors
// by normalized value (not raw string) and rewriting both syntaxes keeps recoloring working
// regardless of which form a given pattern file happens to use.
export function normalizeHexColor(value: string): string | null {
  const hex = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(hex)) {
    return hex;
  }
  if (/^#[0-9a-f]{3}$/.test(hex)) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return null;
}

// Reads the colors actually used in the SVG, rather than trusting the pattern's API-reported
// backgroundColor/accentColors - see the comment in design-3d.component.ts's addPattern() for
// why. Background is identified structurally (a <rect> covering the full canvas - see
// extractBackgroundColor()), not by "whichever color the raw text happens to mention first": a
// <style> block defining an accent's CSS class commonly sits in <defs> before the background
// <rect> even appears in the document, so text order alone picked the wrong one (confirmed live
// against patterns/dots/circles_1.svg, whose accent class is declared before its background rect).
export function extractPatternColors(svg: string): string[] {
  const allColors = extractAllFillColors(svg);
  const backgroundColor = extractBackgroundColor(svg) ?? allColors[0] ?? '#ffffff';
  const accentColors = allColors.filter((color) => color !== backgroundColor);
  return [backgroundColor, ...accentColors];
}

export function extractAllFillColors(svg: string): string[] {
  const regex = /fill(?:="([^"]+)"|:\s*([^;}"]+))/gi;
  const colors: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(svg))) {
    const normalized = normalizeHexColor(match[1] ?? match[2] ?? '');
    if (normalized && !colors.includes(normalized)) {
      colors.push(normalized);
    }
  }
  return colors;
}

// A pattern's background is conventionally a plain <rect> sized to exactly cover the <svg>'s own
// width/height (confirmed against real files) - look for that specific rect's fill rather than
// guessing from color order. Falls back to null (letting extractPatternColors() use the first
// extracted color instead) if no such rect is found, e.g. a pattern with no explicit background
// shape at all.
export function extractBackgroundColor(svg: string): string | null {
  const svgTag = svg.match(/<svg\b[^>]*>/i)?.[0] ?? '';
  const width = svgTag.match(/\bwidth="([\d.]+)"/i)?.[1];
  const height = svgTag.match(/\bheight="([\d.]+)"/i)?.[1];
  const rects = svg.matchAll(/<rect\b([^>]*)\/?>/gi);
  for (const [, attrs] of rects) {
    const rectWidth = attrs.match(/\bwidth="([\d.]+)"/i)?.[1];
    const rectHeight = attrs.match(/\bheight="([\d.]+)"/i)?.[1];
    const fill = attrs.match(/\bfill="([^"]+)"/i)?.[1];
    if (fill && (!width || rectWidth === width) && (!height || rectHeight === height)) {
      return normalizeHexColor(fill);
    }
  }
  return null;
}

export function replaceFillColor(svg: string, from: string, to: string): string {
  const target = normalizeHexColor(from);
  if (!target) {
    return svg;
  }
  let result = svg.replace(/fill="([^"]+)"/gi, (full, value) =>
    normalizeHexColor(value) === target ? `fill="${to}"` : full
  );
  result = result.replace(/fill(:\s*)([^;}"]+)/gi, (full, colonSpace, value) =>
    normalizeHexColor(value) === target ? `fill${colonSpace}${to}` : full
  );
  return result;
}
